import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.first_name}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="page-banner">
        <div className="container-wide">
          <h1>CLIENT LOGIN</h1>
          <div className="breadcrumb">
            <Link to="/">Home</Link> &gt; Login
          </div>
        </div>
      </div>

      <section className="content-section">
        <div className="container-narrow" style={{maxWidth: '400px'}}>
          {/* Login Type Toggle */}
          <div className="flex gap-2 mb-8">
            <button
              className="flex-1 py-3 px-4 bg-[#1a365d] text-white font-semibold text-sm uppercase tracking-wide"
              data-testid="client-login-tab"
            >
              Client Login
            </button>
            <Link
              to="/admin-login"
              className="flex-1 py-3 px-4 bg-[#2d4a6f] text-white font-semibold text-sm uppercase tracking-wide text-center hover:bg-[#1a365d] transition-colors"
              data-testid="admin-login-tab"
            >
              Admin Login
            </Link>
          </div>

          <p className="text-center text-gray-600 mb-8">
            Sign in to access your insurance dashboard.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="form-label">Email Address</label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="btn-navy w-full flex items-center justify-center"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : 'Sign In'}
            </button>
          </form>

          <hr className="content-divider" />

          <p className="text-center text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#1a365d] font-semibold" data-testid="register-link">Create one →</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LoginPage;
